import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getErrorMessage, getFieldErrors } from '@/lib/errors';
import { serversApi } from '@/lib/servers-api';
import { cn } from '@/lib/utils';
import type { ServerGroup } from '@/types/api';

/** Mirrors the colours the domain accepts — anything else is rejected server-side. */
const COLOURS = ['slate', 'blue', 'cyan', 'violet', 'emerald', 'amber', 'rose'] as const;

const SWATCH: Record<string, string> = {
  slate: 'bg-muted-foreground',
  blue: 'bg-primary',
  cyan: 'bg-accent',
  violet: 'bg-[hsl(258_90%_66%)]',
  emerald: 'bg-success',
  amber: 'bg-warning',
  rose: 'bg-danger',
};

export function GroupFormDialog({
  open,
  onOpenChange,
  group,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: ServerGroup | null;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [colour, setColour] = useState<string>('slate');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;

    setFieldErrors({});
    setName(group?.name ?? '');
    setDescription(group?.description ?? '');
    setColour(group?.colour ?? 'slate');
  }, [open, group]);

  const save = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim(), description, colour };
      return group ? serversApi.updateGroup(group.id, payload) : serversApi.createGroup(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['server-inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['servers'] });
      onOpenChange(false);
    },
    onError: (error) => setFieldErrors(getFieldErrors(error)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{group ? 'Edit group' : 'Add a group'}</DialogTitle>
          <DialogDescription>
            Group servers by environment or role. Deleting a group never deletes its servers.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setFieldErrors({});
            save.mutate();
          }}
        >
          {save.error && Object.keys(fieldErrors).length === 0 && (
            <Alert variant="danger">{getErrorMessage(save.error)}</Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="gf-name">Name</Label>
            <Input
              id="gf-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
            {fieldErrors.name && <p className="text-xs text-danger">{fieldErrors.name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="gf-desc">Description</Label>
            <Textarea
              id="gf-desc"
              className="min-h-20"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Colour</Label>
            <div className="flex flex-wrap gap-2">
              {COLOURS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setColour(option)}
                  aria-label={option}
                  aria-pressed={colour === option}
                  className={cn(
                    'flex size-8 items-center justify-center rounded-full border-2 transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    colour === option ? 'border-foreground' : 'border-transparent',
                  )}
                >
                  <span className={cn('size-5 rounded-full', SWATCH[option])} />
                </button>
              ))}
            </div>
            {fieldErrors.colour && <p className="text-xs text-danger">{fieldErrors.colour}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending && <Loader2 className="animate-spin" />}
              {group ? 'Save changes' : 'Add group'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
